import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { PromiseStatuses } from "../../../../utils";
import { GetAllGGNotificationMemberDTO, GetAllGGNotificationMemberFiltersDTO, GGNotificationMemberDTO } from "./dataservice/dto";
import { NewGGNotificationMemberService } from "./dataservice/service";

interface GGNotificationMemberState {
    // REQUEST
    memberCreationRequest: GGNotificationMemberDTO

    // STATUS
    memberCreationStatus: PromiseStatuses
    memberDeleteStatus: PromiseStatuses
    getAllMemberStatus: PromiseStatuses
    getMemberByIdStatus: PromiseStatuses

    // RESPONSE
    getAllMemberResponse?: GetAllGGNotificationMemberDTO
    getMemberByIdResponse?: GGNotificationMemberDTO
}

const initialState: GGNotificationMemberState = {
    memberCreationRequest: {},

    memberCreationStatus: 'idle',
    memberDeleteStatus: 'idle',
    getAllMemberStatus: 'idle',
    getMemberByIdStatus: 'idle'
}

export const GetAllGGNotificationMember = createAsyncThunk(
    'GGNotificationMember/GetAll',
    async (filters: GetAllGGNotificationMemberFiltersDTO, thunkApi): Promise<GetAllGGNotificationMemberDTO> => {
        const GGNotificationMemberService = NewGGNotificationMemberService()
        return GGNotificationMemberService.GetAllGGNotificationMember(filters).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

export const GetGGNotificationMemberById = createAsyncThunk(
    'GGNotificationMember/GetById',
    async (id: string, thunkApi): Promise<GGNotificationMemberDTO> => {
        const GGNotificationMemberService = NewGGNotificationMemberService()
        return GGNotificationMemberService.GetGGNotificationMemberById(id).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

export const GGNotificationMemberCreation = createAsyncThunk(
    'GGNotificationMember/Creation',
    async (data: GGNotificationMemberDTO, thunkApi): Promise<void> => {
        const GGNotificationMemberService = NewGGNotificationMemberService()
        return GGNotificationMemberService.CreateGGNotificationMember(data).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

export const GGNotificationMemberDelete = createAsyncThunk(
    'GGNotificationMember/Delete',
    async (id: string, thunkApi): Promise<void> => {
        const GGNotificationMemberService = NewGGNotificationMemberService()
        return GGNotificationMemberService.DeleteGGNotificationMember(id).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })
    },
)

const GGNotificationMemberSlice = createSlice({
    name: 'ggNotificationMember/slice',
    initialState,
    reducers: {
        // REQUEST
        setMemberCreationGGNotificationId: (state, action) => {
            state.memberCreationRequest.ggNotificationId = action.payload
        },
        setMemberCreationGGN: (state, action) => {
            state.memberCreationRequest.ggn = action.payload
        },
        setMemberCreationCompanyName: (state, action) => {
            state.memberCreationRequest.companyName = action.payload
        },
        setMemberCreationSupervisor: (state, action) => {
            state.memberCreationRequest.supervisor = action.payload
        },
        setMemberCreationRegisteredOfficeAddress: (state, action) => {
            state.memberCreationRequest.registeredOfficeAddress = action.payload
        },
        setMemberCreationDescription: (state, action) => {
            state.memberCreationRequest.description = action.payload
        },
        setMemberCreationAddress: (state, action) => {
            state.memberCreationRequest.address = action.payload
        },
        setMemberCreationLatitude: (state, action) => {
            state.memberCreationRequest.latitude = action.payload
        },
        setMemberCreationLongitude: (state, action) => {
            state.memberCreationRequest.longitude = action.payload
        },
        setMemberCreationProduct: (state, action) => {
            state.memberCreationRequest.product = action.payload
        },
        setMemberCreationCropType: (state, action) => {
            state.memberCreationRequest.cropType = action.payload
        },
        setMemberCreationHarvestingPeriod: (state, action) => {
            state.memberCreationRequest.harvestingPeriod = action.payload
        },
        setMemberCreationManipulationType: (state, action) => {
            state.memberCreationRequest.manipulationType = action.payload
        },
        setMemberCreationParallelProperty: (state, action) => {
            state.memberCreationRequest.parallelProperty = action.payload
        },
        setMemberCreationSubcontractedActivity: (state, action) => {
            state.memberCreationRequest.subcontractedActivity = action.payload
        },
        setMemberCreationCoopModule: (state, action) => {
            state.memberCreationRequest.coopModule = action.payload
        },
        setMemberCreationGraspModule: (state, action) => {
            state.memberCreationRequest.graspModule = action.payload
        },
        setMemberCreationSpringModule: (state, action) => {
            state.memberCreationRequest.springModule = action.payload
        },
        setMemberCreationDestinationCountry: (state, action) => {
            state.memberCreationRequest.destinationCountry = action.payload
        },
        resetMemberCreationRequest: (state) => {
            state.memberCreationRequest = {}
        },

        // STATUS
        resetMemberCreationStatus: (state) => {
            state.memberCreationStatus = 'idle'
        },
        resetGetAllMemberStatus: (state) => {
            state.getAllMemberStatus = 'idle'
        },
        resetGetMemberByIdStatus: (state) => {
            state.getMemberByIdStatus = 'idle'
        },
        resetMemberDeleteStatus: (state) => {
            state.memberDeleteStatus = 'idle'
        }
    },
    extraReducers(builder) {
        builder

            // GET ALL
            .addCase(GetAllGGNotificationMember.pending, (state) => {
                state.getAllMemberStatus = 'loading'
            })
            .addCase(GetAllGGNotificationMember.fulfilled, (state, action) => {
                state.getAllMemberStatus = 'successfully'
                state.getAllMemberResponse = action.payload
            })
            .addCase(GetAllGGNotificationMember.rejected, (state, action) => {
                state.getAllMemberStatus = 'failed'
            })

            // GET BY ID
            .addCase(GetGGNotificationMemberById.pending, (state) => {
                state.getMemberByIdStatus = 'loading'
            })
            .addCase(GetGGNotificationMemberById.fulfilled, (state, action) => {
                state.getMemberByIdStatus = 'successfully'
                state.getMemberByIdResponse = action.payload
            })
            .addCase(GetGGNotificationMemberById.rejected, (state, action) => {
                state.getMemberByIdStatus = 'failed'
            })

            // CREATION
            .addCase(GGNotificationMemberCreation.pending, (state) => {
                state.memberCreationStatus = 'loading'
            })
            .addCase(GGNotificationMemberCreation.fulfilled, (state) => {
                state.memberCreationStatus = 'successfully'
            })
            .addCase(GGNotificationMemberCreation.rejected, (state, action) => {
                state.memberCreationStatus = 'failed'
            })

            // DELETE
            .addCase(GGNotificationMemberDelete.pending, (state) => {
                state.memberDeleteStatus = 'loading'
            })
            .addCase(GGNotificationMemberDelete.fulfilled, (state) => {
                state.memberDeleteStatus = 'successfully'
            })
            .addCase(GGNotificationMemberDelete.rejected, (state, action) => {
                state.memberDeleteStatus = 'failed'
            })
    },
})

export const {
    setMemberCreationAddress,
    setMemberCreationCompanyName,
    setMemberCreationCoopModule,
    setMemberCreationCropType,
    setMemberCreationDescription,
    setMemberCreationDestinationCountry,
    setMemberCreationGGN,
    setMemberCreationGGNotificationId,
    setMemberCreationGraspModule,
    setMemberCreationHarvestingPeriod,
    setMemberCreationLatitude,
    setMemberCreationLongitude,
    setMemberCreationManipulationType,
    setMemberCreationParallelProperty,
    setMemberCreationProduct,
    setMemberCreationRegisteredOfficeAddress,
    setMemberCreationSpringModule,
    setMemberCreationSubcontractedActivity,
    setMemberCreationSupervisor,
    resetGetAllMemberStatus,
    resetGetMemberByIdStatus,
    resetMemberCreationRequest,
    resetMemberCreationStatus,
    resetMemberDeleteStatus
} = GGNotificationMemberSlice.actions

export default GGNotificationMemberSlice.reducer